import type { ComponentPage } from '../../data'
import { Badge, EmptyState, Panel, Section } from '../primitives'
import type { Tone } from '../primitives'
import { Link } from '../shell/Link'

/**
 * The read-only blocks of a part's page: where it sits in the robot, what is
 * fitted inside it, and where it has been. Each is a panel of rows — one
 * border around the list, not a card per line.
 */

const LOCATION: Readonly<Record<string, { label: string; tone: Tone }>> = {
  installed: { label: 'Fitted', tone: 'healthy' },
  spares: { label: 'On the shelf', tone: 'info' },
  depot: { label: 'At the depot', tone: 'warning' },
  scrapped: { label: 'Scrapped', tone: 'critical' },
}

export function LocationBadge({ location }: { location: string | null }): JSX.Element {
  if (location === null) return <Badge tone="warning">Location unknown</Badge>
  const l = LOCATION[location]
  return <Badge tone={l?.tone ?? 'neutral'}>{l?.label ?? location}</Badge>
}

/** Robot › arm › this part. Reached by scanning anything inside and walking up. */
export function Breadcrumb({ component }: { component: ComponentPage }): JSX.Element | null {
  if (component.ancestors.length === 0) return null
  return (
    <nav className="crumbs" aria-label="Where it is fitted">
      {component.ancestors.map((a) => (
        <span key={a.componentId} className="crumbs__step">
          <Link to={{ name: 'component', id: a.componentId }}>{a.fleetNumber ?? `${a.typeName} ${a.serial}`}</Link>
          <span aria-hidden="true">›</span>
        </span>
      ))}
      <span>{component.typeName}</span>
    </nav>
  )
}

export function Positions({ component: c }: { component: ComponentPage }): JSX.Element | null {
  if (c.positions.length === 0) return null
  const filled = c.positions.filter((p) => p.filledBy !== null).length
  return (
    <Section label={`What’s in it · ${filled} of ${c.positions.length}`}>
      <Panel>
        <div className="row-list">
          {c.positions.map((p) =>
            p.filledBy ? (
              <Link key={p.name} to={{ name: 'component', id: p.filledBy.id }} className="row-item">
                <span className="row-item__main">
                  <span className="row-item__title">{p.name.replace(/_/g, ' ')}</span>
                  <span className="row-item__meta">{p.filledBy.typeName}</span>
                </span>
                <span className="row-item__side">
                  {p.filledBy.deviates ? <Badge tone="warning">Deviation</Badge> : null}
                  <span className="serial">{p.filledBy.serial}</span>
                </span>
              </Link>
            ) : (
              <div key={p.name} className="row-item row-item--empty">
                <span className="row-item__main">
                  <span className="row-item__title">{p.name.replace(/_/g, ' ')}</span>
                  <span className="row-item__meta">Empty — takes a {p.expectedTypeName}</span>
                </span>
              </div>
            ),
          )}
        </div>
      </Panel>
    </Section>
  )
}

export function History({ component: c }: { component: ComponentPage }): JSX.Element {
  return (
    <Section label="Where it has been">
      <Panel>
        {c.history.length === 0 ? (
          <EmptyState title="Never fitted to anything" />
        ) : (
          <div className="row-list">
            {c.history.map((h, i) => (
              <div key={`${h.parentSerial}-${h.installedAt}-${i}`} className="row-item">
                <span className="row-item__main">
                  <span className="row-item__title">
                    {h.parentLabel} <span className="muted">· {h.position.replace(/_/g, ' ')}</span>
                  </span>
                  {h.deviationReason ? <span className="row-item__meta warn">Deviation: {h.deviationReason}</span> : null}
                </span>
                <span className="row-item__side">
                  {h.installedAt.slice(0, 10)} → {h.removedAt ? `${h.removedAt.slice(0, 10)} (${h.removalReason})` : 'now'}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </Section>
  )
}
